import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";

export default function AuthPage() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [bgImage, setBgImage] = useState("");
  const navigate = useNavigate();
  const { sendCode, login } = useAuth();

  useEffect(() => {
    API.get("/landing").then(r => setBgImage(r.data.image_url || "")).catch(() => {});
  }, []);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Ingresa tu email");
    setLoading(true);
    try {
      const data = await sendCode(email.trim());
      toast.success("C\u00f3digo enviado a tu correo");
      if (data.dev_code) {
        toast.info(`Modo desarrollo - C\u00f3digo: ${data.dev_code}`, { duration: 15000 });
      }
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar c\u00f3digo");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Ingresa el c\u00f3digo");
    setLoading(true);
    try {
      const user = await login(email.trim(), code.trim());
      toast.success("Bienvenido!");
      if (user.role === "admin") navigate("/admin");
      else if (user.role === "sede") navigate("/sede");
      else navigate("/mis-reservas");
    } catch (err) {
      toast.error(err.response?.data?.detail || "C\u00f3digo inv\u00e1lido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center" data-testid="auth-page">
      {/* Background matching landing page */}
      {bgImage && <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${bgImage})` }} />}
      <div className="absolute inset-0 bg-[#0f0f1a]/90" />

      <div className="relative z-10 w-full max-w-md px-4">
        <Button
          data-testid="back-to-home-btn"
          variant="ghost"
          className="mb-6 text-[#a0a0b8] hover:text-[#f0ece2] hover:bg-[#c9a84c]/15"
          onClick={() => navigate("/")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
        </Button>

        <div className="bg-[#1a1a2e] border border-[rgba(201,168,76,0.15)] rounded-xl p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#c9a84c]/10 flex items-center justify-center mx-auto mb-4">
              {step === 1 ? <Mail className="w-7 h-7 text-[#c9a84c]" /> : <KeyRound className="w-7 h-7 text-[#c9a84c]" />}
            </div>
            <h2 className="text-2xl font-bold text-[#f0ece2] font-template">
              {step === 1 ? "Acceder" : "Verificar c\u00f3digo"}
            </h2>
            <p className="text-sm text-[#a0a0b8] mt-2">
              {step === 1 ? "Ingresa tu correo para recibir un c\u00f3digo de acceso" : `Ingresa el c\u00f3digo enviado a ${email}`}
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-[#a0a0b8] text-xs uppercase tracking-wider">Correo electr&oacute;nico</Label>
                <Input
                  data-testid="email-input"
                  id="email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-1 bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2] placeholder:text-[#555] focus:border-[#c9a84c]"
                />
              </div>
              <Button
                data-testid="send-code-btn"
                type="submit"
                className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold"
                disabled={loading}
              >
                {loading ? "Enviando..." : "Enviar c\u00f3digo"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <Label htmlFor="code" className="text-[#a0a0b8] text-xs uppercase tracking-wider">C&oacute;digo de 5 d&iacute;gitos</Label>
                <Input
                  data-testid="code-input"
                  id="code"
                  type="text"
                  placeholder="12345"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  maxLength={5}
                  className="mt-1 bg-[#252542] border-[rgba(201,168,76,0.2)] text-[#f0ece2] text-center text-2xl tracking-[0.5em] font-bold focus:border-[#c9a84c]"
                />
              </div>
              <Button
                data-testid="verify-code-btn"
                type="submit"
                className="w-full bg-[#c9a84c] text-[#0f0f1a] hover:bg-[#d4b55b] font-semibold"
                disabled={loading}
              >
                {loading ? "Verificando..." : "Verificar"}
              </Button>
              <Button
                data-testid="resend-code-btn"
                type="button"
                variant="ghost"
                className="w-full text-[#a0a0b8] hover:text-[#f0ece2] hover:bg-[#c9a84c]/15"
                onClick={() => setStep(1)}
              >
                Cambiar email o reenviar
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
